import ExtendableObject from "./ExtendableObject";
import { assign, throttle, addClass, removeClass, toggleClass } from "./common";
import History from "./History";

import Tool from "./Tool";
import BlurTool from "./tools/Blur";
import CropTool from "./tools/Crop";
import RotateTool from "./tools/Rotate";
import FlipTool from "./tools/Flip";
import MetaTool from "./tools/Meta";

const TOOLS = {
  blur: BlurTool,
  crop: CropTool,
  rotate: RotateTool,
  flip: FlipTool,
  meta: MetaTool
};

function getElement(el) { return typeof el === "string" ? document.getElementById(el) : el; }

function closest(el, fn) {
  while(el && el.nodeType === Node.ELEMENT_NODE) {
    const res = fn(el);
    if(res) return res;
    el = el.parentNode;
  }
  return null;
}

function clamp(val, min, max) {
  return val < min ? min : val > max ? max : val;
}

const dummyLink = document.createElement("a");

function cacheBust(url) {
  dummyLink.href = url;
  dummyLink.search += (dummyLink.search ? "&" : "?") + "random=" + Math.random().toFixed(6).substring(2);
  return dummyLink.href;
}

function drag({ el, onStart, onMove, onEnd }) {
  let startX, startY;
  let lastX, lastY;

  function start(x, y, evt) {
    onStart(x, y);
    startX = x;
    startY = y;
    document.addEventListener("mousemove", onMousemove);
    document.addEventListener("touchmove", onTouchmove);
    document.addEventListener("mouseup", onMouseup);
    document.addEventListener("touchend", onTouchend);
    document.addEventListener("touchcancel", onTouchend);
    evt.stopPropagation();
    evt.preventDefault();
  }

  function move(x, y, evt) {
    onMove(x - startX, y - startY);
    evt.stopPropagation();
    evt.preventDefault();
    lastX = x;
    lastY = y;
  }

  function end(x, y) {
    onEnd(x - startX, y - startY);
    document.removeEventListener("mousemove", onMousemove);
    document.removeEventListener("touchmove", onTouchmove);
    document.removeEventListener("mouseup", onMouseup);
    document.removeEventListener("touchend", onTouchend);
    document.removeEventListener("touchcancel", onTouchend);
  }

  function abort() {
    end(lastX, lastY);
  }

  function onMousemove(evt) { move(evt.clientX, evt.clientY, evt); }
  function onTouchmove(evt) { move(evt.touches[0].clientX, evt.touches[0].clientY, evt); }
  function onMousedown(evt) { start(evt.clientX, evt.clientY, evt); }
  function onTouchstart(evt) { if(evt.touches.length === 1) start(evt.touches[0].clientX, evt.touches[0].clientY, evt); }
  function onMouseup(evt) { end(evt.clientX, evt.clientY); }
  function onTouchend(evt) { if(evt.touches.length === 0) end(lastX, lastY); }

  el.addEventListener("mousedown", onMousedown);
  el.addEventListener("touchstart", onTouchstart);

  return abort;
}

function pinch({ el, onStart, onMove, onEnd }) {
  let ids = [];

  const coords = new Array(4); // helper

  function fillCoords(evt) {
    for(let i = 0; i < evt.touches.length; i++) {
      const touch = evt.touches[i];
      const idx = ids.indexOf(touch.identifier);
      if(idx < 0) continue;
      coords[idx * 2] = touch.clientX;
      coords[idx * 2 + 1] = touch.clientY;
    }
  }

  function getDist() {
    const dx = coords[2] - coords[0];
    const dy = coords[3] - coords[1];
    return Math.sqrt(dx * dx + dy * dy);
  }

  function getX() {
    return (coords[0] + coords[2]) / 2;
  }

  function getY() {
    return (coords[1] + coords[3]) / 2;
  }

  let startDist, startX, startY;

  function touchstart(evt) {
    if(ids.length === 2) return;
    const changedIDs = Array.prototype.slice.call(evt.changedTouches).map(touch => touch.identifier);
    ids = [...ids, ...changedIDs].slice(0, 2);
    if(ids.length === 2) {
      fillCoords(evt);
      startDist = getDist();
      startX = getX();
      startY = getY();
      onStart(startX, startY);
    }
  }

  function touchmove(evt) {
    if(ids.length !== 2) return;
    fillCoords(evt);
    const dist = getDist();
    const x = getX();
    const y = getY();
    onMove(dist / startDist, x - startX, y - startY);
  }

  function touchend(evt) {
    let ended = false;

    for(let i = 0; i < evt.changedTouches.length; i++) {
      const touch = evt.changedTouches[i];
      let idx = ids.indexOf(touch.identifier);
      if(idx >= 0) {
        ids.splice(idx, 1);
        ended = true;
      }
    }

    if(ended) onEnd();
  }

  el.addEventListener("touchstart", touchstart);
  el.addEventListener("touchmove", touchmove);
  document.addEventListener("touchend", touchend);
  document.addEventListener("touchcancel", touchend);
}

function Selection(editor, sx, sy, ex = sx, ey = sy) {
  this.editor = editor;
  this.sx = sx;
  this.sy = sy;
  this.ex = ex;
  this.ey = ey;
  this.ratios = [0, Infinity];
  this.initDOM();
  this.applyDims();
}

Selection.prototype.applyDims = function() {

  function format(val) { return (val * 100).toFixed(5) + "%"; }

  const x = this.getX();
  const y = this.getY();
  const w = this.getW();
  const h = this.getH();

  this.rect.setAttribute("x", format(x));
  this.rect.setAttribute("y", format(y));
  this.rect.setAttribute("width", format(w));
  this.rect.setAttribute("height", format(h));

  this.handles.tl.setAttribute("cx", format(this.sx));
  this.handles.tl.setAttribute("cy", format(this.sy));
  this.handles.tr.setAttribute("cx", format(this.ex));
  this.handles.tr.setAttribute("cy", format(this.sy));
  this.handles.bl.setAttribute("cx", format(this.sx));
  this.handles.bl.setAttribute("cy", format(this.ey));
  this.handles.br.setAttribute("cx", format(this.ex));
  this.handles.br.setAttribute("cy", format(this.ey));
};

Selection.prototype.getX = function() { return Math.min(this.sx, this.ex); };
Selection.prototype.getY = function() { return Math.min(this.sy, this.ey); };
Selection.prototype.getW = function() { return Math.max(this.sx, this.ex) - this.getX(); };
Selection.prototype.getH = function() { return Math.max(this.sy, this.ey) - this.getY(); };

Selection.prototype.setEnd = function(x, y) {
  const { ex, ey } = this;
  this.ex = x;
  this.ey = y;
  this.checkRatios();
  if(this.isContained()) {
    this.applyDims();
  } else {
    this.ex = ex;
    this.ey = ey;
  }
};

Selection.prototype.destroy = function() {
  const parentNode = this.el.parentNode;
  if(parentNode) parentNode.removeChild(this.el);
};

Selection.prototype.initDOM = function() {
  this.el = document.createElementNS("http://www.w3.org/2000/svg", "g");

  this.rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
  this.rect.setAttribute("class", "photo-editor-selection");
  this.rect.setAttribute("transform", "translate(0.5 0.5)");
  this.el.appendChild(this.rect);

  this.handles = {};

  ["tl", "tr", "bl", "br"].forEach(pos => {
    const el = this.handles[pos] = document.createElementNS("http://www.w3.org/2000/svg", "circle");
    el.setAttribute("class", `photo-editor-handle ${pos}`);
    el.setAttribute("r", "20");
    el.setAttribute("style", "fill: white; stroke: black; stroke-width: 2;");
    this.el.appendChild(el);

    let startSX, startSY, startEX, startEY;

    const onMove = (dx, dy) => {
      const { width, height } = this.editor.dims;
      const { sx, sy, ex, ey } = this;

      switch(pos[1]) {
        case "l": this.sx = startSX + dx / width; break;
        case "r": this.ex = startEX + dx / width; break;
      }

      switch(pos[0]) {
        case "t": this.sy = startSY + dy / height; break;
        case "b": this.ey = startEY + dy / height; break;
      }

      this.checkRatios(pos);
      if(this.isContained()) {
        this.applyDims();
      } else {
        this.sx = sx;
        this.sy = sy;
        this.ex = ex;
        this.ey = ey;
      }
    };

    drag({
      el,
      onStart: () => {
        startSX = this.sx;
        startSY = this.sy;
        startEX = this.ex;
        startEY = this.ey;
      },
      onMove,
      onEnd: onMove
    });
  });

  this.rect.addEventListener("dblclick", evt => {
    if(this.onAction) this.onAction(evt);
  });

  let startSX, startSY, startEX, startEY;

  drag({
    el: this.rect,
    onStart: () => {
      startSX = this.sx;
      startSY = this.sy;
      startEX = this.ex;
      startEY = this.ey;
    },

    onMove: (dx, dy) => {
      const { sx, sy, ex, ey } = this;
      const { width, height } = this.editor.dims;

      this.sx = startSX + dx / width;
      this.sy = startSY + dy / height;
      this.ex = startEX + dx / width;
      this.ey = startEY + dy / height;

      if(this.isContained()) {
        this.applyDims();
      } else {
        this.sx = sx;
        this.sy = sy;
        this.ex = ex;
        this.ey = ey;
      }
    },

    onEnd: () => {}
  });
};

Selection.prototype.checkRatios = function(move = "br") {
  const imgW = this.editor.dims.width;
  const imgH = this.editor.dims.height;

  let w = (this.ex - this.sx) * imgW;
  let h = (this.ey - this.sy) * imgH;

  const ratio = h ? Math.abs(w / h) : Infinity;
  const [min, max] = this.ratios;

  const whSgn = w*h < 0 ? -1 : 1;

  // grow
  if(ratio < min) { w = h * min * whSgn; }
  if(ratio > max) { h = w / max * whSgn; }

  // shrink
  // if (ratio < min) { h = w / min * whSgn; }
  // if (ratio > max) { w = h * max * whSgn; }

  switch(move[1]) {
    case "l": this.sx = this.ex - w / imgW; break;
    case "r": this.ex = this.sx + w / imgW; break;
  }

  switch(move[0]) {
    case "t": this.sy = this.ey - h / imgH; break;
    case "b": this.ey = this.sy + h / imgH; break;
  }
};

Selection.prototype.isContained = function() {
  return this.sx >= 0
  && this.sx <= 1
  && this.ex >= 0
  && this.ex <= 1
  && this.sy >= 0
  && this.sy <= 1
  && this.ey >= 0
  && this.ey <= 1;
};

Selection.prototype.checkContainment = function(move = "br") {
  let w = this.getW();
  let h = this.getH();

  if(w > 1) {
    switch(move[1]) {
      case "l":
        this.sx = this.ex + (this.sx - this.ex) / w;
        this.sy = this.ey + (this.sy - this.ey) / w;
        break;
      case "r":
        this.ex = this.sx + (this.ex - this.sx) / w;
        this.ey = this.sy + (this.ey - this.sy) / w;
        break;
    }
    h /= w;
    w = 1;
  }

  if(h > 1) {
    switch(move[0]) {
      case "t":
        this.sx = this.ex + (this.sx - this.ex) / h;
        this.sy = this.ey + (this.sy - this.ey) / h;
        break;
      case "b":
        this.ex = this.sx + (this.ex - this.sx) / h;
        this.ey = this.sy + (this.ey - this.sy) / h;
        break;
    }
    w /= h;
    h = 1;
  }

  const x = this.getX();
  const y = this.getY();

  if(x < 0) {
    this.sx -= x;
    this.ex -= x;
  }

  if(x + w > 1) {
    const off = x + w - 1;
    this.sx -= off;
    this.ex -= off;
  }

  if(y < 0) {
    this.sy -= y;
    this.ey -= y;
  }

  if(y + h > 1) {
    const off = y + h - 1;
    this.sy -= off;
    this.ey -= off;
  }
};

Selection.prototype.setRatios = function(min, max) {
  this.ratios[0] = min;
  this.ratios[1] = max;
  this.checkRatios();
  this.checkContainment();
  this.applyDims();
};

let uid = 1;

const PhotoEditor = ExtendableObject.extend({
  constructor(opts) {
    this.id = uid++;
    this.hash = `photo-editor-${this.id}`;
    this.opts = assign(this.getDefaultOptions(), opts);
    this.template = new VOID.Template();
    this.template.addTemplate("base", getElement(this.opts.template).innerHTML);
    this.image = new Image();
    this.loaderImage = new Image();
    this.loaderImage.addEventListener("load", evt => this.handleLoad(evt));
    this.imageHolder = document.createElement("div");
    this.imageHolder.style.position = "relative";
    this.imageHolder.appendChild(this.image);
    this.graphicsEl = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    this.graphicsEl.style.position = "absolute";
    this.graphicsEl.style.zIndex = "1";
    this.bindPointerEvents();
    this.imageHolder.appendChild(this.graphicsEl);
    this.dims = {};
    this.selection = null;
    this.activeTool = new Tool(this);
    this.isFit = false;
    this.loaded = false;
    this.selectionRatios = [0, Infinity];
    this.history = new History();

    window.addEventListener("resize", throttle(evt => this.handleResize(evt)));

    this.constructDOM();
    this.initTools();

    this.activeTool.enter();
  },

  getAJAXScope() {
    return this.opts.AJAXScope || "get";
  },

  open(url, meta) {
    this.meta = meta;
    this.setURL(url);
    this.dims = {};
    this.history.clear();
    addClass(this.el, "open");
    addClass(document.body, "oh");
    this.handleResize();
    this.activeTool.open();
    location.hash = this.hash;
  },

  close() {
    removeClass(this.el, "open");
    removeClass(document.body, "oh");
    exitFullscreen();
    if(this.onclose) this.onclose();
    if(location.hash === this.hash) location.hash = "";
  },

  zoom(cx, cy, coeff) {
    if(!this.loaded) return;
    const { width: w, height: h, maxWidth: maxW, maxHeight: maxH, left: l, top: t, origWidth, origHeight } = this.dims;
    let nextW = w * coeff;
    let nextH = h * coeff;

    if(nextW > origWidth * 3) {
      nextW = origWidth * 3;
      nextH = origHeight * 3;
    }

    if(nextW < Math.min(100, origWidth)) {
      nextW = 100;
      nextH = h * nextW / w;
    }

    if(nextH < Math.min(100, origHeight)) {
      nextH = 100;
      nextW = w * nextH / h;
    }

    coeff = nextW / w;

    const nextL = nextW > maxW ? clamp(l + cx * (1 - coeff), -Infinity, 0) : (maxW - nextW) / 2;
    const nextT = nextH > maxH ? clamp(t + cy * (1 - coeff), -Infinity, 0) : (maxH - nextH) / 2;
    this.dims.width = nextW;
    this.dims.height = nextH;
    this.dims.left = nextL;
    this.dims.top = nextT;
    this.applyDims();
    this.isFit = false;
    this.oneBtn.disabled = false;
    this.fitBtn.disabled = false;
  },

  pan(x, y) {
    if(this.dims.width > this.dims.maxWidth) this.dims.left = clamp(this.dims.left + x, this.dims.maxWidth - this.dims.width, 0);
    if(this.dims.height > this.dims.maxHeight) this.dims.top = clamp(this.dims.top + y, this.dims.maxHeight - this.dims.height, 0);
    this.applyDims();
    this.isFit = false;
    this.oneBtn.disabled = false;
    this.fitBtn.disabled = false;
  },

  fitImage() {
    if(!this.loaded) return;
    let { origWidth: w, origHeight: h } = this.dims;
    const { maxWidth: maxW, maxHeight: maxH } = this.dims;

    if(w > maxW) {
      h = h * maxW / w;
      w = maxW;
    }

    if(h > maxH) {
      w = w * maxH / h;
      h = maxH;
    }

    w = Math.floor(w);
    h = Math.floor(h);

    this.dims.width = w;
    this.dims.height = h;
    this.dims.left = (maxW - w) / 2;
    this.dims.top = (maxH - h) / 2;

    this.applyDims();

    this.isFit = true;

    this.fitBtn.disabled = true;
    this.oneBtn.disabled = false;
  },

  centerImage() {
    if(!this.loaded) return;
    const w = this.dims.width = this.dims.origWidth;
    const h = this.dims.height = this.dims.origHeight;
    this.dims.left = Math.round((this.dims.maxWidth - w) / 2);
    this.dims.top = Math.round((this.dims.maxHeight - h) / 2);
    this.isFit = false;
    this.applyDims();
    this.oneBtn.disabled = true;
    this.fitBtn.disabled = false;
  },

  adjustPosition() {
    if(!this.loaded) return;
    const { width, height, maxWidth, maxHeight } = this.dims;
    this.dims.left = (width < maxWidth) ? (maxWidth - width) / 2 : 0;
    this.dims.top = (height < maxHeight) ? (maxHeight - height) / 2 : 0;
    this.applyDims();
  },

  getDefaultOptions() {
    return {};
  },

  setURL(url) {
    this.loaded = false;
    this.el.classList.add("loading");
    this.loaderImage.src = cacheBust(url);
    if(this.loaderImage.complete) this.handleLoad();
  },

  applyDims() {
    if(!this.loaded) return;
    const width = Math.round(this.dims.width);
    const height = Math.round(this.dims.height);
    const left = Math.round(this.dims.left);
    const top = Math.round(this.dims.top);
    const { maxWidth, maxHeight } = this.dims;
    this.imageHolder.style.width = `${width}px`;
    // this.imageHolder.style.height = `${height}px`;
    this.graphicsEl.setAttribute("width", width);
    this.graphicsEl.setAttribute("height", height);
    this.graphicsEl.setAttribute("viewbox", `0 0 ${width} ${height}`);

    if(width < maxWidth) {
      this.imageHolder.style.left = `${left}px`;
    } else {
      this.imageHolder.style.left = null;
      this.imagePane.scrollLeft = -left;
    }

    if(height < maxHeight) {
      this.imageHolder.style.top = `${top}px`;
    } else {
      this.imageHolder.style.top = null;
      this.imagePane.scrollTop = -top;
    }

    this.zoomIndicator.innerText = Math.floor(this.dims.width / this.dims.origWidth * 100) + "%";
  },

  bindPointerEvents() {
    let startX, startY;
    let offX, offY;

    const abortDrag = drag({
      el: this.graphicsEl,
      onStart: (x, y) => {
        offX = this.imagePane.offsetLeft + this.dims.left;
        offY = this.imagePane.offsetTop + this.dims.top;
        startX = x;
        startY = y;
        this.activeTool.dragStart(x - offX, y - offY);
      },
      onMove: (x, y) => {
        this.activeTool.dragMove(startX + x - offX, startY + y - offY);
      },
      onEnd: (x, y) => {
        this.activeTool.dragEnd(startX + x - offX, startY + y - offY);
      }
    });

    pinch({
      el: this.graphicsEl,
      onStart: (x, y) => {
        abortDrag();
        offX = this.imagePane.offsetLeft + this.dims.left;
        offY = this.imagePane.offsetTop + this.dims.top;
        startX = x;
        startY = y;
        this.activeTool.pinchStart(x - offX, y - offY);
      },
      onMove: (dist, x, y) => {
        offX = this.imagePane.offsetLeft + this.dims.left;
        offY = this.imagePane.offsetTop + this.dims.top;
        this.activeTool.pinchMove(dist, startX + x - offX, startY + y - offY);
      },
      onEnd: (x, y) => {
        this.activeTool.pinchEnd(startX + x - offX, startY + y - offY);
      }
    });
  },

  handleResize() {
    this.dims.maxWidth = this.imagePane.clientWidth;
    this.dims.maxHeight = this.imagePane.clientHeight;
    if(this.isFit) this.fitImage();
    else this.adjustPosition();
  },

  handleLoad() {
    this.loaded = true;
    this.clearSelection();
    this.image.src = this.loaderImage.src;

    const width = this.loaderImage.width;
    const height = this.loaderImage.height;

    if(width !== this.dims.origWidth || height !== this.dims.origHeight) {
      this.dims.origWidth = this.dims.width = width;
      this.dims.origHeight = this.dims.height = height;
      this.dims.left = this.dims.top = 0;
      this.fitImage();
    }

    this.el.classList.remove("loading");
  },

  handleMouseWheel(evt) {
    if(evt.ctrlKey) {
      const offX = this.imagePane.offsetLeft + this.dims.left;
      const offY = this.imagePane.offsetTop + this.dims.top;
      const x = evt.clientX - offX;
      const y = evt.clientY - offY;
      this.activeTool.mouseWheel(x, y, evt.deltaY);
      evt.preventDefault();
      return false;
    }
  },

  handlePaneScroll(evt) {
    if(this.dims.width >= this.dims.maxWidth) this.dims.left = -this.imagePane.scrollLeft;
    if(this.dims.height >= this.dims.maxHeight) this.dims.top = -this.imagePane.scrollTop;
    evt.preventDefault();
    return false;
  },

  handleClose() {
    history.back();
    // this.close();
  },

  handleFull() {
    if(isFullscreen()) {
      exitFullscreen();
      removeClass(this.el, "full");
    }
    else {
      requestFullscreen(this.el);
      addClass(this.el, "full");
    }
  },

  handleSelectionAction() {
    this.activeTool.selectionAction();
  },

  getElement(id) {
    return document.getElementById(`${this.opts.idPrefix}-${this.id}-${id}`);
  },

  setTool: function(name) {
    this.activeTool.leave();
    const tool = this.tools[name];
    this.activeTool = (tool === this.activeTool) ? new Tool(this) : tool;
    this.activeTool.enter();
  },

  startSelection(x, y) {
    if(!this.loaded) return;
    x /= this.dims.width;
    y /= this.dims.height;
    this.clearSelection();
    this.selection = new Selection(this, x, y);
    this.selection.setRatios(...this.selectionRatios);
    this.selection.onAction = () => { this.handleSelectionAction(); };
    this.graphicsEl.appendChild(this.selection.el);
  },

  setSelectionEnd(x, y) {
    if(!this.loaded) return;
    x = clamp(x / this.dims.width, 0, 1);
    y = clamp(y / this.dims.height, 0, 1);
    this.selection.setEnd(x, y);
  },

  setSelectionRatios(min, max) {
    this.selectionRatios[0] = min;
    this.selectionRatios[1] = max;
    if(this.selection) this.selection.setRatios(min, max);
  },

  clearSelection() {
    if(this.selection) {
      this.selection.destroy();
      this.selection = null;
    }
  },

  constructDOM: function() {
    const dummyEl = document.createElement("div");

    dummyEl.innerHTML = this.template.parse("base", {
      id: this.id
    });

    while(dummyEl.firstChild) document.body.appendChild(dummyEl.firstChild);

    this.el = document.getElementById(`photo-editor-${this.id}`);

    this.el.addEventListener("click", evt => {
      const tool = closest(evt.target, el => el.getAttribute("data-editor-tool"));
      if(tool) this.setTool(tool);
    });

    this.imagePane = this.getElement("image");
    this.imagePane.appendChild(this.imageHolder);
    this.imagePane.addEventListener("wheel", evt => this.handleMouseWheel(evt));
    this.imagePane.addEventListener("scroll", evt => this.handlePaneScroll(evt));

    const closeBtn = this.getElement("close");
    if(closeBtn) closeBtn.addEventListener("click", evt => { this.handleClose(evt); });
    const fullBtn = this.getElement("full");
    if(fullBtn) {
      if(window.requestFullscreen && window.requestFullscreen !== window.noop) {
        fullBtn.addEventListener("click", evt => { this.handleFull(evt); });
      } else {
        fullBtn.style.display = "none";
      }
    }
    this.oneBtn = this.getElement("one");
    if(this.oneBtn) this.oneBtn.addEventListener("click", () => { this.centerImage(); });
    this.fitBtn = this.getElement("fit");
    if(this.fitBtn) this.fitBtn.addEventListener("click", () => { this.fitImage(); });

    const revertBtn = this.getElement("revert");
    if(revertBtn) revertBtn.addEventListener("click", () => { this.revert(); });
    const undoBtn = this.getElement("undo");
    if(undoBtn) undoBtn.addEventListener("click", () => { this.undo(); });
    const redoBtn = this.getElement("redo");
    if(redoBtn) redoBtn.addEventListener("click", () => { this.redo(); });

    this.zoomIndicator = this.getElement("zoom_indicator");

    this.history.onchange = () => {
      toggleClass(undoBtn, "act", this.history.hasUndo());
      toggleClass(redoBtn, "act", this.history.hasRedo());
      toggleClass(revertBtn, "act", !this.history.state.meta.isRevert && (this.history.hasUndo() || !this.meta || this.meta.hasFilters));
    };

    this.toolboxEl = this.getElement("toolbox");

    window.addEventListener("hashchange", () => {
      if(location.hash !== "#" + this.hash) this.close();
    })
  },

  makeURL(search) {
    const link = document.createElement("a");
    link.href = this.opts.urlPrefix;
    link.search += (link.search ? "&" : "?") + search;
    return link.href;
  },

  makeHistoryURL(filters) {
    const filterPart = filters.map
      ? filters
        .map((f, i) => `filter[${i}]=${f}`)
        .join("&")
    : "";

    return this.makeURL("Act=addfilter&" + filterPart);
  },

  undo() {
    if(!this.history.hasUndo()) return;
    const url = this.history.getUndoURL() || this.makeURL("Act=removefilters&nr=1");

    AJAX.send({
      scope: this.getAJAXScope(),
      callback: (data) => {
        const hasFilters = !!(data.original.dbentry.filters && data.original.dbentry.filters.length);
        const showOriginalWhenNoFilter = this.opts.showOriginalWhenNoFilter;
        this.setURL((hasFilters || !showOriginalWhenNoFilter) ? data.pics.http : data.original.http);
        this.history.commitUndo(data.removedFilters && this.makeHistoryURL(data.removedFilters));
      },
      url
    });
  },

  redo() {
    if(!this.history.hasRedo()) return;
    const url = this.history.getRedoURL();

    AJAX.send({
      scope: this.getAJAXScope(),
      callback: (data) => {
        this.setURL(data.pics.http);
        this.history.commitRedo();
      },
      url
    });
  },

  revert() {
    const url = this.makeURL("Act=removefilters&nr=all");

    AJAX.send({
      scope: this.getAJAXScope(),
      callback: (data) => {
        const hasFilters = !!(data.original.dbentry.filters && data.original.dbentry.filters.length);
        const showOriginalWhenNoFilter = this.opts.showOriginalWhenNoFilter;
        this.setURL((hasFilters || !showOriginalWhenNoFilter) ? data.pics.http : data.original.http);
        const state = this.history.push(this.makeHistoryURL(data.removedFilters), { isRevert: true });
        state.redo = url;
      },
      url
    });
  },

  initTools() {
    this.tools = {};
    let html = "";

    Object.keys(TOOLS).forEach(name => {
      if(name === "meta" && !this.opts.withMeta) return;
      this.tools[name] = new TOOLS[name](this);
      html += this.template.parse(`tool_${name}`, { id: this.id });
    });

    this.toolboxEl.innerHTML = html;

    let lastEl = null;

    Object.keys(this.tools).forEach(name => {
      const el = this.getElement(name);
      this.tools[name].bindEvents(el, () => {
        if(lastEl) removeClass(lastEl, "sel");
        this.setTool(name);
        if(lastEl !== el) addClass(lastEl = el, "sel");
        else lastEl = null;
      });
    });
  },

  destroy() {
    this.el.parentNode.removeChild(this.el);
    if(this.onclose) this.onclose();
  }
});

export default PhotoEditor;
